/**
 * MyRaffle.jsx — personal raffle status across the guilds the viewer
 * tracks. Card per guild with tickets / entries / gold / rank.
 *
 * Settings:
 *   guilds      — array of guild keys ('post' / 'dealers')
 *   weekOffset  — 0=this week, 1=last week, etc.
 */

import { Show, For, createMemo } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import { gold, qty, weekLabel } from '../lib/format.js';
import './MyRaffle.css';

const GUILD_LABEL = { post: 'Trading Post', dealers: 'Dealers' };
const GUILD_EMOJI = { post: '🛒', dealers: '💼' };

export default function MyRaffle(props) {
  const params = createMemo(() => ({
    guilds: (props.settings?.guilds || []).join(','),
    weekOffset: props.settings?.weekOffset || 0,
  }));
  const { data, loading, error } = useWidgetData('my-raffle', params);
  const guilds = () => data()?.guilds || [];
  const note = () => data()?.note;
  const wkLabel = () => weekLabel(props.settings?.weekOffset || 0);

  return (
    <div class="v2-widget v2-myraffle">
      <div class="v2-widget-hdr">{wkLabel()}</div>
      <Show when={!loading()} fallback={<div class="v2-widget-skel">Loading…</div>}>
        <Show when={!error()} fallback={<div class="v2-error">{error()}</div>}>
          <Show when={!note()} fallback={<div class="v2-empty">{note()}</div>}>
            <Show when={guilds().length} fallback={<div class="v2-empty">No tracked guilds. Open ⚙ Settings to pick.</div>}>
              <div class="v2-widget__scroll">
                <div class="v2-myraffle__grid">
                  <For each={guilds()}>{(g) => <GuildCard g={g} />}</For>
                </div>
              </div>
            </Show>
          </Show>
        </Show>
      </Show>
    </div>
  );
}

function GuildCard(props) {
  const g = props.g;
  const ticketShare = () => {
    if (!g.totalTickets || !g.myTickets) return 0;
    return Math.round((g.myTickets / g.totalTickets) * 1000) / 10;
  };
  const hasData = () => g.weekStart != null && (g.myEntries || g.totalEntrants);
  return (
    <div class="v2-myraffle-card">
      <div class="v2-myraffle-card__head">
        <span class="v2-myraffle-card__emoji">{GUILD_EMOJI[g.guild] || '🎟️'}</span>
        <span class="v2-myraffle-card__name">Redfur {GUILD_LABEL[g.guild] || g.guild}</span>
        <Show when={g.myRank}>
          <span class="v2-myraffle-card__rank">#{g.myRank}</span>
        </Show>
      </div>
      <Show when={hasData()} fallback={<div class="v2-myraffle-card__empty">No raffle data for this week.</div>}>
        <div class="v2-myraffle-card__stats">
          <div class="v2-myraffle-stat v2-myraffle-stat--gold">
            <span class="v2-myraffle-stat__label">My tickets</span>
            <span class="v2-myraffle-stat__value">{qty(g.myTickets)}</span>
            <Show when={ticketShare()}>
              <span class="v2-myraffle-stat__sub">{ticketShare()}% of pool</span>
            </Show>
          </div>
          <div class="v2-myraffle-stat">
            <span class="v2-myraffle-stat__label">Entries</span>
            <span class="v2-myraffle-stat__value">{qty(g.myEntries)}</span>
            <Show when={g.myVoided}>
              <span class="v2-myraffle-stat__sub">{qty(g.myVoided)} voided</span>
            </Show>
          </div>
          <div class="v2-myraffle-stat v2-myraffle-stat--cyan">
            <span class="v2-myraffle-stat__label">Gold spent</span>
            <span class="v2-myraffle-stat__value">{gold(g.myGold)}</span>
          </div>
          <div class="v2-myraffle-stat">
            <span class="v2-myraffle-stat__label">Pool</span>
            <span class="v2-myraffle-stat__value">{qty(g.totalTickets)}</span>
            <span class="v2-myraffle-stat__sub">{qty(g.totalEntrants)} entrants</span>
          </div>
        </div>
      </Show>
    </div>
  );
}
